import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star, Phone, Scissors, Lightbulb, EyeOff, Trophy } from "lucide-react";
import axios from "axios";
import Marquee from "../components/Marquee";
import HolidayCanvas from "../components/HolidayCanvas";
import { Reveal, Overline } from "../components/Reveal";
import { IMAGES, SERVICES, API, PHONE, PHONE_TEL } from "../data";

const HERO_LINES = ["LIFE LOOKS", "BETTER IN", "GLOW MODE."];

// Holiday season: Oct 15 through Jan 5 — snow + twinkling strings dress the hero
const isHolidaySeason = () => {
  const now = new Date();
  const m = now.getMonth();
  const d = now.getDate();
  return (m === 9 && d >= 15) || m === 10 || m === 11 || (m === 0 && d <= 5);
};

const WHY = [
  { icon: Scissors, title: "Custom-Cut To Your Home", text: "Every strand measured and cut to fit your exact roofline. No sagging, no gaps, no compromise." },
  { icon: Lightbulb, title: "Commercial-Grade Bulbs", text: "Pro-grade lighting engineered to outlast the season. No burnt-out bulbs killing your display." },
  { icon: EyeOff, title: "Zero Visible Wires", text: "Clean, hidden installs. All you see is the glow — never the wiring behind it." },
  { icon: Trophy, title: "Price-Beat Guarantee", text: "Bring any competitor's quote and we beat it. Every time. That's the GlowMode promise." },
];

export default function Home() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 200]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0.35]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setReviews((r.data.reviews || []).slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="absolute inset-0 scale-110">
          <img src={IMAGES.hero} alt="Luxury home glowing at dusk with architectural lighting" className="h-full w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/50 via-obsidian/60 to-obsidian" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(233,199,103,0.12),transparent_55%)]" />
        {isHolidaySeason() && (
          <HolidayCanvas mode="warm" className="absolute inset-0 h-full w-full pointer-events-none opacity-80" />
        )}

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24 pt-40 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="text-xs sm:text-sm font-mono uppercase tracking-[0.35em] text-gold mb-6"
          >
            Salt Lake County's Luxury Lighting Studio
          </motion.p>
          <h1 className="font-serif font-bold tracking-tight leading-[1.05] text-5xl sm:text-6xl lg:text-8xl text-slate-100">
            {HERO_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "text-gold-gradient" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
          >
            Residential & commercial lighting installation — permanent trim, holiday displays, landscape, weddings, and more. Designed for your property. Cut to fit. Built to wow.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/book"
              data-testid="hero-book-now-button"
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-obsidian bg-gradient-to-br from-gold-light via-gold to-gold-dark transition-all duration-300 hover:shadow-[0_0_35px_rgba(233,199,103,0.5)] hover:-translate-y-0.5"
            >
              Get a Free Quote
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/holiday"
              data-testid="hero-holiday-promo-button"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-silver-light border border-silver/30 glass-panel transition-all duration-300 hover:border-gold/60 hover:text-gold-light hover:-translate-y-0.5"
            >
              Holiday Promo — 50% Off Install
            </Link>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* SERVICES PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 sm:py-28">
        <Reveal>
          <Overline>What we light</Overline>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-slate-100 tracking-tight max-w-xl">
              Every space deserves its <span className="text-gold-gradient">glow</span>
            </h2>
            <Link to="/services" data-testid="home-view-all-services-link" className="group inline-flex items-center gap-2 text-sm font-semibold text-gold hover:text-gold-light transition-colors">
              View all services
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.slice(0, 4).map((s, i) => (
            <Reveal key={s.id} delay={i * 0.08} className={i === 0 ? "sm:col-span-2 sm:row-span-2" : ""}>
              <Link
                to="/services"
                data-testid={`home-service-card-${s.id}`}
                className={`group relative block overflow-hidden rounded-2xl border border-white/5 transition-all duration-300 hover:border-gold/50 hover:shadow-[0_10px_30px_rgba(0,0,0,0.5)] hover:-translate-y-1 ${i === 0 ? "h-full min-h-[420px]" : "h-52"}`}
              >
                <img src={s.img} alt={s.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/30 to-transparent" />
                <div className="absolute bottom-0 p-5">
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold mb-1">{s.tag}</p>
                  <h3 className={`font-serif font-semibold text-slate-100 ${i === 0 ? "text-2xl" : "text-lg"}`}>{s.name}</h3>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY GLOWMODE */}
      <section className="border-y border-white/5 bg-charcoal/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 sm:py-28">
          <Reveal>
            <Overline>The GlowMode standard</Overline>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-slate-100 tracking-tight mb-14 max-w-2xl">
              Obsessed with the details others skip
            </h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.08}>
                <div data-testid={`why-card-${i}`} className="h-full rounded-2xl border border-white/5 bg-card2/60 p-8 transition-all duration-300 hover:border-gold/50 hover:shadow-[0_10px_30px_rgba(0,0,0,0.5)] hover:-translate-y-1">
                  <w.icon className="h-8 w-8 text-gold mb-5" />
                  <h3 className="font-serif text-xl font-medium text-slate-100 mb-3">{w.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOLIDAY PROMO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 sm:py-28">
        <Reveal>
          <div data-testid="home-holiday-promo" className="relative overflow-hidden rounded-3xl border border-gold/25">
            <img src={IMAGES.holidayTree} alt="Home wrapped in warm Christmas lights" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/85 to-obsidian/30" />
            <div className="relative p-8 sm:p-14 lg:p-20 max-w-2xl">
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-4">Limited-Time Holiday Offer</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-slate-100 leading-tight mb-4">
                <span className="text-gold-gradient">50% OFF</span> installation
              </h2>
              <p className="text-slate-300 text-base sm:text-lg leading-relaxed mb-8">
                Book your holiday lighting before November 1st and save half on installation. Custom cut, commercial grade, free takedown after the season.
              </p>
              <Link
                to="/holiday"
                data-testid="home-holiday-cta-button"
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-obsidian bg-gradient-to-br from-gold-light via-gold to-gold-dark transition-all duration-300 hover:shadow-[0_0_35px_rgba(233,199,103,0.5)] hover:-translate-y-0.5"
              >
                Claim the Offer
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* REVIEWS TEASER */}
      <section className="border-t border-white/5 bg-charcoal/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 sm:py-28">
          <Reveal>
            <Overline>Word on the street</Overline>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-slate-100 tracking-tight">
                Neighbors love the glow
              </h2>
              <Link to="/reviews" data-testid="home-all-reviews-link" className="group inline-flex items-center gap-2 text-sm font-semibold text-gold hover:text-gold-light transition-colors">
                Read all reviews
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {reviews.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.08}>
                <div data-testid={`home-review-card-${i}`} className="h-full rounded-2xl border border-white/5 bg-card2/60 p-8">
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star key={s} className={`h-4 w-4 ${s < r.rating ? "text-gold fill-gold" : "text-slate-600"}`} />
                    ))}
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed mb-6 line-clamp-4">"{r.text}"</p>
                  <p className="text-sm font-semibold text-slate-100">{r.name}</p>
                  <p className="text-xs text-slate-500">{r.location}{r.service ? ` · ${r.service}` : ""}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 sm:py-28 text-center">
        <Reveal>
          <Overline>Ready when you are</Overline>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-serif font-bold text-slate-100 tracking-tight mb-8">
            Let's put your home in <span className="text-gold-gradient">Glow Mode</span>
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/book"
              data-testid="home-cta-book-button"
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-obsidian bg-gradient-to-br from-gold-light via-gold to-gold-dark transition-all duration-300 hover:shadow-[0_0_35px_rgba(233,199,103,0.5)] hover:-translate-y-0.5"
            >
              Book Now
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href={PHONE_TEL}
              data-testid="home-cta-call-button"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-silver-light border border-silver/30 glass-panel transition-all duration-300 hover:border-gold/60 hover:text-gold-light hover:-translate-y-0.5"
            >
              <Phone className="h-4 w-4 text-gold" /> {PHONE}
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
